// src/lib/api/onboardingApi.ts

import { getApiClient } from './client';
import {
    OnboardingProgram,
    EmployeeOnboarding,
    ProgramEmployee,
    ProgramProgress,
    CreateProgramRequest,
    AssignOnboardingRequest,
    CreateOnboardingModuleRequest,
    ModuleGenerationRequest,
    GeneratedModule,
    ModuleStepInput,
    RegenerateStepRequest,
} from '../models/onboarding';
import { Module } from '../models/module';

// Employer: program management
const createProgram = async (data: CreateProgramRequest): Promise<OnboardingProgram> => {
    return await getApiClient().post<OnboardingProgram>('/api/onboarding/programs', data);
};

const getPrograms = async (): Promise<{ programs: OnboardingProgram[]; total: number; assigned_counts?: Record<string, number> }> => {
    try {
        return await getApiClient().get<{ programs: OnboardingProgram[]; total: number; assigned_counts?: Record<string, number> }>('/api/onboarding/programs');
    } catch {
        return { programs: [], total: 0 };
    }
};

const getProgramById = async (id: string): Promise<OnboardingProgram> => {
    return await getApiClient().get<OnboardingProgram>(`/api/onboarding/programs/${id}`);
};

// Employer: edit a program (C1) — full replace of name/description/modules/default
const updateProgram = async (id: string, data: CreateProgramRequest): Promise<OnboardingProgram> => {
    return await getApiClient().put<OnboardingProgram>(`/api/onboarding/programs/${id}`, data);
};

// Employer: delete a program (C1). Assignments cascade-delete.
const deleteProgram = async (id: string): Promise<void> => {
    await getApiClient().delete(`/api/onboarding/programs/${id}`);
};

const getProgramEmployees = async (programId: string): Promise<{ employees: ProgramEmployee[]; total: number }> => {
    try {
        return await getApiClient().get<{ employees: ProgramEmployee[]; total: number }>(`/api/onboarding/programs/${programId}/employees`);
    } catch {
        return { employees: [], total: 0 };
    }
};

// Employer: remove an onboarding assignment (B7)
const unassignOnboarding = async (assignmentId: string): Promise<void> => {
    await getApiClient().delete(`/api/onboarding/assignments/${assignmentId}`);
};

// Employer: create a new onboarding module with steps
const createOnboardingModule = async (data: CreateOnboardingModuleRequest): Promise<Module> => {
    return await getApiClient().post<Module>('/api/onboarding/modules', data);
};

// A source a course references, slim {id, name} (backend services.SourceBrief).
export interface ModuleSourceBrief {
    id: string;
    name: string;
}

// Where a course is used across the org (#40): onboarding programs that include
// it + job postings that require it as an application prerequisite.
export interface ModuleUsage {
    programs: { id: string; name: string }[];
    needs: { id: string; role: string; status: string }[];
}

// Employer: list onboarding modules scoped to their organisation.
//   sources_by_module: module id → the sources it references (clickable on cards).
//   step_counts:       module id → { step_type → count } (the step breakdown).
const getOnboardingModules = async (): Promise<{
    modules: Module[];
    total: number;
    sources_by_module?: Record<string, ModuleSourceBrief[]>;
    step_counts?: Record<string, Record<string, number>>;
    usage_by_module?: Record<string, ModuleUsage>;
}> => {
    try {
        return await getApiClient().get<{
            modules: Module[];
            total: number;
            sources_by_module?: Record<string, ModuleSourceBrief[]>;
            step_counts?: Record<string, Record<string, number>>;
            usage_by_module?: Record<string, ModuleUsage>;
        }>('/api/onboarding/modules');
    } catch {
        return { modules: [], total: 0 };
    }
};

// Employer: update an existing onboarding module (replaces basics + all steps)
const updateOnboardingModule = async (id: string, data: CreateOnboardingModuleRequest): Promise<Module> => {
    return await getApiClient().put<Module>(`/api/onboarding/modules/${id}`, data);
};

// Employer: delete an onboarding module
const deleteOnboardingModule = async (id: string): Promise<void> => {
    await getApiClient().delete(`/api/onboarding/modules/${id}`);
};

// Employer: AI-generate a module (returns generated structure, NOT saved to DB)
// Uses an extended timeout (3 minutes) since AI generation can take longer than the default 30s.
const aiGenerateModule = async (data: ModuleGenerationRequest): Promise<GeneratedModule> => {
    return await getApiClient().post<GeneratedModule>('/api/onboarding/modules/ai-generate', data, {
        timeout: 180_000, // 3 minutes
    });
};

// Employer: regenerate a single step (V1.1) — returns the new step shape;
// caller merges it back into the in-progress module state. Per-step requests
// are smaller than full module generations so a tighter 90s timeout is fine.
const aiRegenerateStep = async (data: RegenerateStepRequest): Promise<ModuleStepInput> => {
    return await getApiClient().post<ModuleStepInput>('/api/onboarding/modules/ai-regenerate-step', data, {
        timeout: 90_000,
    });
};

// ── Inline Link description assist (course description) ────────────────
// The module cousin of needApi.aiAssistNeed: revise ONE course-description
// Markdown block per a quick action or free-form instruction. Returns revised
// markdown (NOT saved) for preview-then-apply. Backs the 3b course edit
// surface's description LinkAssistPopover + selection assist.
export type ModuleAssistAction =
    | 'rewrite_formal'
    | 'rewrite_engaging'
    | 'shorten'
    | 'proofread'
    | 'expand'
    | 'custom';

export interface ModuleAssistRequest {
    action: ModuleAssistAction;
    content: string;         // current markdown / selected excerpt (may be empty)
    instruction?: string;    // free text (expand/custom)
    module_name?: string;    // optional context (the course name)
    scope?: 'selection' | 'full'; // 'selection' = revise only the excerpt
}

const aiAssistModule = async (data: ModuleAssistRequest): Promise<string> => {
    const result = await getApiClient().post<{ content: string }>('/api/onboarding/modules/ai-assist', data, {
        timeout: 90_000,
    });
    return result.content;
};

// "Link" AI assist for the program builder (optional, preview-before-apply).
// One endpoint, action-driven; the response carries only the fields relevant
// to the requested action, plus a short human-readable rationale.
export type ProgramAssistAction = 'improve_description' | 'reorder' | 'suggest_courses';

export interface ProgramAssistCourse {
    id: string;
    name: string;
    description?: string;
    estimated_duration?: string;
}

export interface ProgramAssistRequest {
    action: ProgramAssistAction;
    name: string;
    description?: string;
    courses: ProgramAssistCourse[];      // current ordered courses in the program
    available?: ProgramAssistCourse[];   // library courses not yet added (for suggest_courses)
}

export interface ProgramAssistResponse {
    improved_description?: string;       // improve_description
    ordered_course_ids?: string[];       // reorder (full set of current ids, reordered)
    add_course_ids?: string[];           // suggest_courses
    remove_course_ids?: string[];        // suggest_courses
    rationale?: string;                  // short explanation shown in the preview
}

// Employer: ask "Link" to assist with the program. Returns a proposal only —
// nothing is persisted until the user applies it and saves the program.
const assistProgram = async (data: ProgramAssistRequest): Promise<ProgramAssistResponse> => {
    return await getApiClient().post<ProgramAssistResponse>('/api/onboarding/programs/assist', data, {
        timeout: 120_000, // 2 minutes — single LLM call
    });
};

// Employer: assign onboarding
const assignOnboarding = async (data: AssignOnboardingRequest): Promise<EmployeeOnboarding> => {
    return await getApiClient().post<EmployeeOnboarding>('/api/onboarding/assign', data);
};

// Employee: view my onboarding
const getMyOnboarding = async (): Promise<{ onboardings: EmployeeOnboarding[]; total: number }> => {
    try {
        return await getApiClient().get<{ onboardings: EmployeeOnboarding[]; total: number }>('/api/my/onboarding');
    } catch {
        return { onboardings: [], total: 0 };
    }
};

const getMyOnboardingProgress = async (onboardingId: string): Promise<ProgramProgress> => {
    return await getApiClient().get<ProgramProgress>(`/api/my/onboarding/${onboardingId}/progress`);
};

// Per-program onboarding detail for one employee (employer detail view).
export interface EmployeeOnboardingDetail {
    program_id: string;
    program_name: string;
    status: string;
    completed: number;
    total: number;
    percentage: number;
}

const getEmployeeOnboarding = async (userId: string): Promise<EmployeeOnboardingDetail[]> => {
    try {
        const res = await getApiClient().get<{ onboardings: EmployeeOnboardingDetail[]; total: number }>(
            `/api/employees/${userId}/onboarding`
        );
        return res.onboardings ?? [];
    } catch {
        return [];
    }
};

export const onboardingApi = {
    createProgram,
    getPrograms,
    getEmployeeOnboarding,
    getProgramById,
    updateProgram,
    deleteProgram,
    getProgramEmployees,
    assignOnboarding,
    unassignOnboarding,
    createOnboardingModule,
    getOnboardingModules,
    updateOnboardingModule,
    deleteOnboardingModule,
    aiGenerateModule,
    aiRegenerateStep,
    aiAssistModule,
    assistProgram,
    getMyOnboarding,
    getMyOnboardingProgress,
};
